import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ReportFiltersComponent from "../../ReportFiltersComponent";
import ReportScreen from "../../../../../common/enums/ReportScreens";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import ReportFiltersLoading from "../../ReportFiltersLoading";
import { useEffect, useState } from "react";
import { ReportFilters } from "../../../../../domain/models/report-filters";
import moment from "moment";
import WarningIcon from "@mui/icons-material/Warning";
import { ExpenseDashBoardStore } from "../../../../../store/main/ExpenseDashBoardStore";
import PendingApprovalConfirmationModal from "../Modal/PendingApprovalConfirmationModal";
import MissingLocationStatusModal from "../Modal/MissingLocationStatusModal";
import Swal from "sweetalert2";
import FileReadManagerModal from "../Modal/FileReadManagerModal";

type UploadedFile = {
  id: number;
  expense_id: number;
  file_uuid: string;
  uploaded_by: number;
  expense_date: string;
  status: string;
};

type ManagerExpenseApprovalRow = {
  id: number;
  uploadedFiles: UploadedFile[];
  user_id: number;
  user_name: string;
  user_role: string;
  reporting_date: string;
  city_category: string;
  brick_type: string;
  total_distance_travel_km: string;
  daily_allowance: string;
  traveling_allowance: string;
  fix_allowance: string | null;
  food_allowance: string | null;
  remark: string;
  status: string;
  expected_amount: string;
  approved_status: string;
  system_calculated_amount: string;
  attendance_type: string | null;
  final_amount: string;
  brick_name: string;
  system_remarks: string;
  approved_by: number | null;
  approver_name: string | null;
  miscellaneous_expense_type_id: number | null;
  miscellaneous_expense_type: string | null;
  miscellaneous_expense_amount: number | null;
  expense_uploaded_files?: UploadedFile[];
};

type ManagerExpenseApprovalRows = {
  empRole: string;
  uploadedFiles: UploadedFile[];
  fullName: string;
  id: number;
  date: string;
  station: string | null;
  locality: string | null;
  totalDistanceKm: string;
  dailyAllowance: string;
  travelAllowance: string;
  fixAllowance: string | null;
  foodAllowance: string | null;
  remark: string | null;
  status: string;
  expectedAmount: string | null;
  attendanceType: string;
  approvedStatus: string;
  total: string;
  totalFinalAmount: string;
  workingBrick: string | null;
  userId: number;
  systemRemarks: string | null;
  miscellaneousExpenseTypeId: number | null;
  miscellaneousExpenseType: string | null;
  miscellaneousExpenseAmount: number | null;
  miscellaneousExpenseAmountError?: string;
  approvedBy?: number | null;
  approverName?: string | null;
};

type ManagerExpenseApproval = {
  name: string;
  expense_approval: ManagerExpenseApprovalRow[];
};

type ManagerApprovalResponse = {
  success: boolean;
  manager_expense_approval: ManagerExpenseApproval;
};

type ManagerRemarksData = {
  month: string;
  year: number;
  expenses: ManagerAddRemark[];
};

type ManagerAddRemark = {
  expense_id: number;
  user_id: number;
  remark: string;
  expected_amount: number | string | null;
  miscellaneous_expense_type_id?: number | null;
  miscellaneous_expense_amount?: number | null;
};

type CorrectRemarkPayload = {
  expense_id: number;
  user_id: number;
  miscellaneous_expense_type_id?: number | null;
  miscellaneous_expense_amount?: number | null;
};

type ExpenseAPIResponse = {
  success: boolean;
  miscellaneous_expense_types: ExpenseReason[];
};

type ExpenseReason = {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
};

const commonTableStyle = {
  padding: "2px 4px",
  fontSize: "0.75rem",
  lineHeight: 1.0,
  textAlign: "left",
};

const ManagerExpenseApproval = () => {
  const { userDetails, userRole, decodeToken } = CustomerDatabaseStore();
  const {
    fetchManagerExpenseApproval,
    FetchManagerExpenseApprovalState,
    FetchManagerExpenseApproval,
    addManagerCommentAmountExpense,
    fetchAddExpenseReason,
  } = ExpenseDashBoardStore();

  const currentDate = new Date();
  const [month, setMonth] = useState<number>(currentDate.getMonth() + 1);
  const [year, setYear] = useState<number>(currentDate.getFullYear());
  const currentMonthName = new Date(year, month - 1).toLocaleString("default", {
    month: "long",
  });
  const previousMonthDate = new Date(currentDate);
  previousMonthDate.setMonth(currentDate.getMonth() - 1);
  const prevMonthName = previousMonthDate.toLocaleString("default", {
    month: "long",
  });
  const isOldMonth = () => {
    const selectedDate = new Date(year, month - 1);
    const current = new Date();
    const currentMinusOne = new Date(
      current.getFullYear(),
      current.getMonth() - 1
    );

    // Disable if selected month is before previous month
    return selectedDate < currentMinusOne;
  };

  const isDisabled = isOldMonth();

  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [tableApiData, setTableApiData] = useState<
    ManagerExpenseApprovalRows[]
  >([]);
  const [tableMockApiData, setTableMockApiData] = useState<
    ManagerExpenseApprovalRows[]
  >([]);
  const [fetchApiResponse, setFetchApiResponse] =
    useState<ManagerApprovalResponse | null>(null);
  const [showTotalFinalAmount, setShowTotalFinalAmount] =
    useState<boolean>(false);
  const [openPendingModalIndex, setOpenPendingModalIndex] = useState<
    number | null
  >(null);
  const [selectedSystemRemarks, setSelectedSystemRemarks] =
    useState<string>("");
  const [openSystemRemarkModal, setOpenSystemRemarkModal] =
    useState<boolean>(false);
  const [managerAddRemarks, setManagerAddRemarks] = useState<
    ManagerAddRemark[]
  >([]);
  const [apiLoading, setApiLoading] = useState<boolean>(true);
  const [expenseReasons, setExpenseReasons] = useState<ExpenseReason[]>([]);
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const [openMyModal, setOpenMyModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<{
    id: number;
    uuids: string[];
  } | null>(null);

  const handleOpenMyModal = (row: ManagerExpenseApprovalRows) => {
    setSelectedRow({
      id: row.id!,
      uuids: row.uploadedFiles?.map((f) => f.file_uuid) ?? [],
    });

    setOpenMyModal(true);
  };

  const handleCloseMyModal = () => {
    setOpenMyModal(false);
  };

  useEffect(() => {
    if (!openMyModal) {
      refreshTableAfterModalClose();
    }
  }, [openMyModal]);

  const refreshTableAfterModalClose = async () => {
    const monthString = month.toString().padStart(2, "0");
    const response = await fetchManagerExpenseApproval({
      month: monthString,
      year,
    });

    if (response?.success) {
      const fetchData = response.manager_expense_approval.expense_approval;

      const mappedApiData = fetchData.map(
        (expense: ManagerExpenseApprovalRow) => ({
          id: expense.id,
          uploadedFiles: expense.expense_uploaded_files ?? [],
          userId: expense.user_id,
          fullName: expense.user_name,
          empRole: expense.user_role,
          date: expense.reporting_date,
          workingBrick: expense.brick_name ?? "-",
          station: expense.brick_type ?? "-",
          locality: expense.city_category ?? "-",
          totalDistanceKm: parseFloat(expense.total_distance_travel_km),
          dailyAllowance: parseFloat(expense.daily_allowance),
          travelAllowance: parseFloat(expense.traveling_allowance),
          attendanceType: expense.attendance_type ?? "-",
          fixAllowance: expense.fix_allowance
            ? parseFloat(expense.fix_allowance)
            : null,
          foodAllowance: expense.food_allowance
            ? parseFloat(expense.food_allowance)
            : null,
          total: Number(expense.system_calculated_amount).toFixed(2),
          status: expense.status,
          approvedStatus: expense.approved_status,
          systemRemarks: expense.system_remarks ?? "",
          remark: expense.remark ?? "",
          expectedAmount: expense.expected_amount
            ? parseFloat(expense.expected_amount)
            : null,
          totalFinalAmount: Number(expense.final_amount).toFixed(2) ?? 0,
          miscellaneousExpenseType: expense.miscellaneous_expense_type ?? null,
          miscellaneousExpenseTypeId:
            expense.miscellaneous_expense_type_id ?? null,
          miscellaneousExpenseAmount:
            expense.miscellaneous_expense_amount ?? null,
        })
      );

      setTableApiData(mappedApiData);
    if (selectedRow?.id) {
      const updatedRow = mappedApiData.find((r: any) => r.id === selectedRow.id);
      if (updatedRow) {
        setSelectedRow({
          id: updatedRow.id,
          uuids: updatedRow.uploadedFiles.map((f: any) => f.file_uuid)
        });
      }
    }
    }
  };

  useEffect(() => {
    decodeToken();
    loadExpenseReasons();
  }, []);

  const loadExpenseReasons = async () => {
    try {
      const response = await fetchAddExpenseReason();
      if (response.success) {
        setExpenseReasons(response.miscellaneous_expense_types);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const monthString = month.toString().padStart(2, "0");
    const params = {
      month: monthString,
      year: year,
    };

    fetchManagerExpenseApproval(params);
  }, [month, year]);

  const filterData = async (filter: ReportFilters) => {
    const month = parseInt(moment(filter.date?.toString()).format("MM"));
    const year = parseInt(moment(filter.date?.toString()).format("YYYY"));

    setMonth(month);
    setYear(year);
    const monthString = month.toString().padStart(2, "0");
    const params = {
      month: monthString,
      year: year,
    };
    try {
      const fetchManagerExpenseResponse = await fetchManagerExpenseApproval(
        params
      );
      if (fetchManagerExpenseResponse.success) {
        setFetchApiResponse(fetchManagerExpenseResponse);
        const fetchData =
          fetchManagerExpenseResponse.manager_expense_approval.expense_approval;

        const mappedApiData = fetchData.map(
          (expense: ManagerExpenseApprovalRow) => ({
            id: expense.id,
            uploadedFiles: expense.expense_uploaded_files ?? [],
            userId: expense.user_id,
            fullName: expense.user_name,
            empRole: expense.user_role,
            date: expense.reporting_date,
            workingBrick: expense.brick_name ?? "-",
            station: expense.brick_type ?? "-",
            locality: expense.city_category ?? "-",
            totalDistanceKm: parseFloat(expense.total_distance_travel_km),
            dailyAllowance: parseFloat(expense.daily_allowance),
            travelAllowance: parseFloat(expense.traveling_allowance),
            attendanceType: expense.attendance_type ?? "-",
            fixAllowance: expense.fix_allowance
              ? parseFloat(expense.fix_allowance)
              : null,
            foodAllowance: expense.food_allowance
              ? parseFloat(expense.food_allowance)
              : null,
            total: Number(expense.system_calculated_amount).toFixed(2),
            status: expense.status,
            approvedStatus: expense.approved_status,
            systemRemarks: expense.system_remarks ?? "",
            remark: expense.remark ?? "",
            expectedAmount: expense.expected_amount
              ? parseFloat(expense.expected_amount)
              : null,
            totalFinalAmount: Number(expense.final_amount).toFixed(2) ?? 0,
            miscellaneousExpenseType:
              expense.miscellaneous_expense_type ?? null,
            miscellaneousExpenseTypeId:
              expense.miscellaneous_expense_type_id ?? null,
            miscellaneousExpenseAmount:
              expense.miscellaneous_expense_amount ?? null,
          })
        );

        setTableApiData(mappedApiData);
      }
    } catch {
    } finally {
      setApiLoading(false);
    }
  };

  const clearData = async () => {
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    setMonth(month);
    setYear(year);
    const monthString = month.toString().padStart(2, "0");
    const params = {
      month: monthString,
      year: year,
    };
    try {
      const fetchManagerExpenseResponse = await fetchManagerExpenseApproval(
        params
      );
      if (fetchManagerExpenseResponse.success) {
        setFetchApiResponse(fetchManagerExpenseResponse);
        const fetchData =
          fetchManagerExpenseResponse.manager_expense_approval.expense_approval;

        const mappedApiData = fetchData.map(
          (expense: ManagerExpenseApprovalRow) => ({
            id: expense.id,
            uploadedFiles: expense.expense_uploaded_files ?? [],
            userId: expense.user_id,
            fullName: expense.user_name,
            empRole: expense.user_role,
            date: expense.reporting_date,
            workingBrick: expense.brick_name ?? "-",
            station: expense.brick_type ?? "-",
            locality: expense.city_category ?? "-",
            totalDistanceKm: parseFloat(expense.total_distance_travel_km),
            dailyAllowance: parseFloat(expense.daily_allowance),
            travelAllowance: parseFloat(expense.traveling_allowance),
            attendanceType: expense.attendance_type ?? "-",
            fixAllowance: expense.fix_allowance
              ? parseFloat(expense.fix_allowance)
              : null,
            foodAllowance: expense.food_allowance
              ? parseFloat(expense.food_allowance)
              : null,
            total: Number(expense.system_calculated_amount).toFixed(2),
            status: expense.status,
            approvedStatus: expense.approved_status,
            systemRemarks: expense.system_remarks ?? "",
            remark: expense.remark ?? "",
            expectedAmount: expense.expected_amount
              ? parseFloat(expense.expected_amount)
              : null,
            totalFinalAmount: Number(expense.final_amount).toFixed(2) ?? 0,
            miscellaneousExpenseType:
              expense.miscellaneous_expense_type ?? null,
            miscellaneousExpenseTypeId:
              expense.miscellaneous_expense_type_id ?? null,
            miscellaneousExpenseAmount:
              expense.miscellaneous_expense_amount ?? null,
          })
        );

        setTableApiData(mappedApiData);
      }
    } catch {
    } finally {
      setApiLoading(false);
    }
  };

  const handleSystemRemarkOpenModal = (remarks: string) => {
    setSelectedSystemRemarks(remarks);
    setOpenSystemRemarkModal(true);
  };

  const handleSystemRemarkCloseModal = () => {
    setOpenSystemRemarkModal(false);
    setSelectedSystemRemarks("");
  };

  const handleCommentChange = (index: number, value: string) => {
    const updatedData = [...tableApiData];
    updatedData[index].remark = value;
    setTableApiData(updatedData);

    updateManagerAddRemarks(index, updatedData[index]);
  };

  const updateManagerAddRemarks = (index: number, rowData: any) => {
    if (rowData.id === undefined) return;

    const managerNewRemark: ManagerAddRemark = {
      expense_id: rowData.id,
      user_id: rowData.userId,
      remark: rowData.remark || "",
      expected_amount: rowData.expectedAmount || null,
    };

    const miscTypeId = rowData.miscellaneousExpenseTypeId;
    const miscAmount = rowData.miscellaneousExpenseAmount;

    const isValidType =
      miscTypeId !== null && miscTypeId !== undefined && miscTypeId !== 0;
    const isValidAmount =
      miscAmount !== null && miscAmount !== undefined && miscAmount !== 0;

    if (isValidType && isValidAmount) {
      managerNewRemark.miscellaneous_expense_type_id = miscTypeId;
      managerNewRemark.miscellaneous_expense_amount = miscAmount;
    }

    setManagerAddRemarks((prev) => {
      const exists = prev.find((r) => r.expense_id === rowData.id);
      return exists
        ? prev.map((r) => (r.expense_id === rowData.id ? managerNewRemark : r))
        : [...prev, managerNewRemark];
    });
  };

  const handleExpectedAmountChange = (index: number, value: string) => {
    const updatedData = [...tableApiData];
    updatedData[index].expectedAmount = value ? value : null;
    setTableApiData(updatedData);

    if (updatedData[index].status === "incorrect") {
      updateManagerAddRemarks(index, updatedData[index]);
    }
  };

  const handleExpenseStatusChange = (index: number, value: number | string) => {
    const updatedData = [...tableApiData];
    const selectedValue = Number(value);

    if (!value || selectedValue === 0) {
      updatedData[index].miscellaneousExpenseType = "";
      updatedData[index].miscellaneousExpenseTypeId = null;
      updatedData[index].miscellaneousExpenseAmount = null;
      updatedData[index].miscellaneousExpenseAmountError = "";
    } else {
      updatedData[index].miscellaneousExpenseType = String(value);
      updatedData[index].miscellaneousExpenseTypeId = selectedValue;

      const amount = updatedData[index].miscellaneousExpenseAmount;
      const isAmountEmpty = amount === null || amount === undefined;

      updatedData[index].miscellaneousExpenseAmountError = isAmountEmpty
        ? "Please enter an amount (0 or more)"
        : "";
    }

    setTableApiData(updatedData);
    updateManagerAddRemarks(index, updatedData[index]);
  };

  const handleExpenseExtraAmountChange = (
    index: number,
    value: number | null
  ) => {
    const updatedData = [...tableApiData];
    updatedData[index].miscellaneousExpenseAmount = value;

    // Clear error if valid
    if (
      updatedData[index].miscellaneousExpenseTypeId &&
      value !== null &&
      value !== undefined
    ) {
      updatedData[index].miscellaneousExpenseAmountError = "";
    }

    setTableApiData(updatedData);
    updateManagerAddRemarks(index, updatedData[index]);
  };

  const handleOpenModal = (index: number) => {
    setOpenPendingModalIndex(index);
  };

  // const handlePendingCloseModal = () => {
  //   setOpenPendingModalIndex(null);
  // };

  const handlePendingCloseModal = () => {
    setSubmitInProgress(false);
    setOpenPendingModalIndex(null);
  };

  const hasRowErrors = (row: (typeof tableApiData)[number]): boolean => {
    if (row.status !== "correct") {
      if (!row.remark?.trim()) return true;

      if (
        row.expectedAmount === "" ||
        row.expectedAmount == null ||
        row.expectedAmount === "0"
      )
        return true;
    }

    // Always validate misc type + amount
    const isValidType =
      row.miscellaneousExpenseTypeId !== null &&
      row.miscellaneousExpenseTypeId !== undefined &&
      row.miscellaneousExpenseTypeId !== 0;

    const isAmountValid =
      row.miscellaneousExpenseAmount !== null &&
      row.miscellaneousExpenseAmount !== undefined &&
      Number(row.miscellaneousExpenseAmount) > 0;

    if (isValidType && !isAmountValid) return true;

    return false;
  };

  const handlePendingSubmitModal = async () => {
    if (submitInProgress || openPendingModalIndex === null) return;

    setSubmitInProgress(true);

    const selectedRow = tableApiData[openPendingModalIndex];
    const { status } = selectedRow;

    const miscType = selectedRow.miscellaneousExpenseTypeId;
    const miscAmount = selectedRow.miscellaneousExpenseAmount;

    const isValidType =
      miscType !== null && miscType !== undefined && miscType !== 0;
    const isAmountProvided =
      miscAmount !== null && miscAmount !== undefined && Number(miscAmount) > 0;

    if (isValidType && !isAmountProvided) {
      Swal.fire({
        icon: "warning",
        title: "Missing Miscellaneous Amount",
        text: "Please enter a valid miscellaneous amount greater than 0.",
        confirmButtonColor: "#1976d2",
      });
      setSubmitInProgress(false);
      return;
    }

    let remarkPayload: ManagerAddRemark | CorrectRemarkPayload;

    if (status === "incorrect") {
      if (!selectedRow.remark?.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Missing Remark",
          text: "Please enter a remark before submitting.",
          confirmButtonColor: "#1976d2",
        });
        setSubmitInProgress(false);
        return;
      }

      if (
        selectedRow.expectedAmount == null ||
        selectedRow.expectedAmount === "" ||
        selectedRow.expectedAmount === "0"
      ) {
        Swal.fire({
          icon: "warning",
          title: "Missing Expected Amount",
          text: "Please enter a valid expected amount before submitting.",
          confirmButtonColor: "#1976d2",
        });
        setSubmitInProgress(false);
        return;
      }

      remarkPayload = {
        expense_id: selectedRow.id,
        user_id: selectedRow.userId,
        remark: selectedRow.remark,
        expected_amount: selectedRow.expectedAmount,
      };

      if (isValidType && isAmountProvided) {
        remarkPayload.miscellaneous_expense_type_id = miscType;
        remarkPayload.miscellaneous_expense_amount = Number(miscAmount);
      }
    } else if (status === "correct") {
      remarkPayload = {
        expense_id: selectedRow.id,
        user_id: selectedRow.userId,
      };

      if (isValidType && isAmountProvided) {
        remarkPayload.miscellaneous_expense_type_id = miscType;
        remarkPayload.miscellaneous_expense_amount = Number(miscAmount);
      }
    } else {
      Swal.fire({
        icon: "error",
        title: "Unknown Status",
        text: "Status must be either 'correct' or 'incorrect'.",
        confirmButtonColor: "#d33",
      });
      setSubmitInProgress(false);
      return;
    }

    const finalPayload = {
      month: month.toString().padStart(2, "0"),
      year,
      expenses: [remarkPayload],
    };

    try {
      const response = await addManagerCommentAmountExpense(finalPayload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Submitted!",
          text: "Your remarks have been successfully approved.",
          confirmButtonColor: "#1976d2",
        });

        const updated = [...tableApiData];
        updated.splice(openPendingModalIndex, 1);
        setTableApiData(updated);
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: "Permission Denied",
          confirmButtonColor: "#d33",
        });
      }
      setOpenPendingModalIndex(null);
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: error?.message || "There was an error processing your request.",
        confirmButtonColor: "#d33",
      });
      setOpenPendingModalIndex(null);
    } finally {
      setSubmitInProgress(false);
    }
  };

  const tableHeaders = [
    "Emp Role",
    "Full Name",
    "Date",
    "Working Brick",
    "Station",
    "Locality",
    "Total Distance (km)",
    "Daily Allowance",
    "Travel Allowance",
    "Attendance Type",
    "Total Amount (₹)",
    "Approved Status",
    "Status",
    "System Remarks",
    "Remarks",
    "Expected Amount",
    "Total Final Amount",
    "Expense Reason",
    "Expense Extra Amount",
    "Document Review",
    "Approved Manager Action",
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-start"
        mt={2}
        gap={1}
      >
        {Object.keys(userDetails).length > 0 ? (
          <ReportFiltersComponent
            type={ReportScreen.EXPENSE_DASHBOARD}
            successCallback={filterData}
            clearSuccessCallBack={clearData}
            userRole={userRole.role}
            userDetails={userDetails}
            gridColumns={14}
            territoryMultiSelect={false}
          />
        ) : (
          <ReportFiltersLoading />
        )}
      </Box>
      <Box sx={{ maxHeight: "70vh", mt: 1, paddingBottom: "50px" }}>
        {month === currentDate.getMonth() + 1 &&
        year === currentDate.getFullYear() ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 4,
            }}
          >
            <Typography
              variant="h3"
              color="textSecondary"
              sx={{ textAlign: "center", mt: 6 }}
            >
              Pending Expense Approval for the {currentMonthName} {year} is not
              yet available.
              <br />
              You can check for {prevMonthName} {year}
            </Typography>
          </Box>
        ) : !apiLoading && tableApiData && tableApiData.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            my={4}
          >
            <Typography
              variant="h6"
              sx={{ textAlign: "center", mt: 6, fontSize: "30px" }}
            >
              No Expense Approval Found For {currentMonthName} {year}
            </Typography>
          </Box>
        ) : (
          <>
            <Box>
              {apiLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    my: 4,
                  }}
                >
                  <CircularProgress size={100} />
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "70vh",
                    borderRadius: 4,
                    boxShadow: 2,
                    border: "1px solid #ccc",
                    mb: 4,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          height: 10,
                        }}
                      >
                        {tableHeaders.map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              backgroundColor: "#d9e2ec",
                              fontWeight: 700,
                              fontSize: "0.6rem",
                              color: "#333",
                              position: "sticky",
                              top: 0,
                              height: 10,
                              zIndex: 1,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              paddingTop: "0.5px",
                              paddingBottom: "0.5px",
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableApiData &&
                        tableApiData.length > 0 &&
                        tableApiData.map((row, index) => (
                          <TableRow
                            key={row.date}
                            hover
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: "#fafafa",
                                height: 20,
                              },
                            }}
                          >
                            <TableCell sx={commonTableStyle}>
                              {row.empRole ?? "-"}
                            </TableCell>
                            <TableCell
                              sx={[
                                commonTableStyle,
                                {
                                  width: "auto",
                                  minWidth: "100px",
                                  whiteSpace: "nowrap",
                                },
                              ]}
                            >
                              {row.fullName ?? "-"}
                            </TableCell>
                            <TableCell
                              sx={[
                                commonTableStyle,
                                {
                                  width: "auto",
                                  minWidth: "50px",
                                  whiteSpace: "nowrap",
                                },
                              ]}
                            >
                              {new Date(row.date).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                              })}
                            </TableCell>

                            <TableCell sx={commonTableStyle}>
                              {row.workingBrick ?? "-"}
                            </TableCell>

                            <TableCell sx={commonTableStyle}>
                              {row.station ?? "-"}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.locality ?? "-"}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.totalDistanceKm}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.dailyAllowance}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.travelAllowance}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.attendanceType ?? "-"}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {(() => {
                                const totalNum = parseFloat(row.total);
                                const cappedInt = Math.min(
                                  Math.floor(totalNum),
                                  9999
                                );
                                const decimalPart = totalNum
                                  .toFixed(2)
                                  .split(".")[1];
                                return `${cappedInt}.${decimalPart}`;
                              })()}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.approvedStatus}
                            </TableCell>
                            <TableCell sx={{ minWidth: 80 }}>
                              <Select
                                size="small"
                                value={row.status || "incorrect"}
                                disabled={isDisabled}
                                displayEmpty
                                fullWidth
                                sx={{
                                  backgroundColor: "#fff",
                                  fontSize: "0.7rem",
                                  height: 20,
                                  "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                    minHeight: "unset",
                                    color:
                                      row.status === "correct"
                                        ? "green !important"
                                        : "red !important",
                                    WebkitTextFillColor:
                                      row.status === "correct"
                                        ? "green"
                                        : "red",
                                  },
                                  "&.Mui-disabled .MuiSelect-select": {
                                    color:
                                      row.status === "correct"
                                        ? "green !important"
                                        : "red !important",
                                    WebkitTextFillColor:
                                      row.status === "correct"
                                        ? "green"
                                        : "red",
                                    opacity: 1,
                                  },
                                  "& fieldset": {
                                    borderRadius: 1,
                                  },
                                }}
                              >
                                <MenuItem
                                  value="incorrect"
                                  sx={{ color: "red", fontSize: "0.7rem" }}
                                >
                                  incorrect
                                </MenuItem>
                                <MenuItem
                                  value="correct"
                                  sx={{ color: "green", fontSize: "0.7rem" }}
                                >
                                  correct
                                </MenuItem>
                              </Select>
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "1px 2px",
                                fontSize: "0.75rem",
                                lineHeight: 1.0,
                                textAlign: "center",
                              }}
                            >
                              {row.systemRemarks ? (
                                <>
                                  <IconButton
                                    color="warning"
                                    size="medium"
                                    onClick={() =>
                                      handleSystemRemarkOpenModal(
                                        row.systemRemarks ?? ""
                                      )
                                    }
                                    sx={{
                                      fontSize: "0.5rem",
                                      height: "15px",
                                      width: "15px",
                                    }}
                                  >
                                    <WarningIcon fontSize="small" />
                                  </IconButton>

                                  <MissingLocationStatusModal
                                    open={openSystemRemarkModal}
                                    onClose={handleSystemRemarkCloseModal}
                                    systemRemarks={selectedSystemRemarks}
                                  />
                                </>
                              ) : (
                                "-"
                              )}
                            </TableCell>

                            <TableCell
                              sx={{ minWidth: 100, position: "relative" }}
                            >
                              <Tooltip
                                title={row.remark?.trim() || "No comment added"}
                                placement="top-start"
                                arrow
                                enterDelay={300}
                              >
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={row.remark || ""}
                                  onChange={(e) =>
                                    handleCommentChange(index, e.target.value)
                                  }
                                  error={
                                    !isDisabled &&
                                    row.status !== "correct" &&
                                    !row.remark?.trim()
                                  }
                                  helperText={
                                    !isDisabled &&
                                    row.status !== "correct" &&
                                    !row.remark?.trim()
                                      ? "Remark cannot be empty"
                                      : ""
                                  }
                                  placeholder="Add remark"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                      height: 20,
                                      fontSize: "0.8rem",
                                    },
                                    "& input": {
                                      padding: "4px 8px",
                                    },
                                  }}
                                  disabled={
                                    isDisabled || row.status === "correct"
                                  }
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 100, position: "relative" }}
                            >
                              <TextField
                                type="number"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={row.expectedAmount ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    /^\d{0,4}(?:\.\d{0,2})?$/.test(value) &&
                                    value !== "0"
                                  ) {
                                    handleExpectedAmountChange(index, value);
                                  }
                                }}
                                placeholder="Enter amount"
                                error={
                                  row.status !== "correct" &&
                                  (row.expectedAmount === "" ||
                                    row.expectedAmount == null ||
                                    row.expectedAmount === "0")
                                }
                                helperText={
                                  row.status !== "correct"
                                    ? row.expectedAmount === "" ||
                                      row.expectedAmount == null
                                      ? "Amount is required"
                                      : row.expectedAmount === "0"
                                      ? "Amount cannot be 0"
                                      : ""
                                    : ""
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1,
                                    height: 20,
                                    fontSize: "0.8rem",
                                  },
                                  "& input": {
                                    padding: "4px 8px",
                                  },
                                }}
                                disabled={
                                  isDisabled || row.status === "correct"
                                }
                              />
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.totalFinalAmount ?? 0}
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 100, position: "relative" }}
                            >
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{
                                  backgroundColor: "#fff",
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1,
                                    height: 20,
                                    fontSize: "0.8rem",
                                  },
                                  "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                  },
                                }}
                              >
                                <Select
                                  displayEmpty
                                  disabled={isDisabled}
                                  value={row.miscellaneousExpenseType ?? ""}
                                  onChange={(e) =>
                                    handleExpenseStatusChange(
                                      index,
                                      Number(e.target.value)
                                    )
                                  }
                                  renderValue={(selected) => {
                                    if (!selected) {
                                      return (
                                        <em style={{ color: "#aaa" }}>
                                          Select status
                                        </em>
                                      );
                                    }

                                    const foundReason = expenseReasons.find(
                                      (r) => r.id === Number(selected)
                                    );
                                    return foundReason
                                      ? foundReason.name
                                      : `${selected}`;
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Select status</em>
                                  </MenuItem>
                                  {expenseReasons.length > 0 ? (
                                    expenseReasons.map((reason) => (
                                      <MenuItem
                                        key={reason.id}
                                        value={reason.id}
                                      >
                                        {reason.name}
                                      </MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem disabled>
                                      <em>Loading reasons...</em>
                                    </MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            </TableCell>

                            <TableCell
                              sx={{ minWidth: 100, position: "relative" }}
                            >
                              <TextField
                                type="number"
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Enter extra amount"
                                value={
                                  row.miscellaneousExpenseAmount === null ||
                                  row.miscellaneousExpenseAmount === undefined
                                    ? ""
                                    : row.miscellaneousExpenseAmount.toString()
                                }
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    /^$|^(?:\d{1,4})(?:\.\d{0,2})?$/.test(value)
                                  ) {
                                    handleExpenseExtraAmountChange(
                                      index,
                                      value === "" ? null : Number(value)
                                    );
                                  }
                                }}
                                disabled={
                                  isDisabled || !row.miscellaneousExpenseType
                                }
                                sx={{
                                  backgroundColor: row.miscellaneousExpenseType
                                    ? "#fff"
                                    : "#f0f0f0",
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1,
                                    height: 20,
                                    fontSize: "0.8rem",
                                  },
                                  "& input": {
                                    padding: "4px 8px",
                                  },
                                }}
                                error={
                                  !!row.miscellaneousExpenseType &&
                                  (!row.miscellaneousExpenseAmount ||
                                    Number(row.miscellaneousExpenseAmount) ===
                                      0)
                                }
                                helperText={
                                  !!row.miscellaneousExpenseType &&
                                  (!row.miscellaneousExpenseAmount ||
                                    Number(row.miscellaneousExpenseAmount) ===
                                      0)
                                    ? "Please enter a valid amount greater than 0"
                                    : ""
                                }
                              />
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                sx={{ padding: "1px 4px", maxHeight: "22px" }}
                              onClick={() =>
                                  row.id !== undefined && handleOpenMyModal(row)
                                }
                                disabled={
                                  Number(row.totalDistanceKm) <= 100 ||
                                  row.id === undefined
                                }
                              >
                                View
                              </Button>
                              {selectedRow?.id === row.id && (
                              <FileReadManagerModal
                               open={openMyModal}
                                  onClose={handleCloseMyModal}
                                  rowId={selectedRow?.id ?? null}
                                  uuids={selectedRow?.uuids ?? []}
                                  onSuccess={refreshTableAfterModalClose}
                              />
                                )}
                            </TableCell>
                            {/* <PendingApprovalConfirmationModal
                              open={openPendingModalIndex !== null}
                              onClose={handlePendingCloseModal}
                              onSubmit={handlePendingSubmitModal}
                            /> */}
                            <PendingApprovalConfirmationModal
                              open={openPendingModalIndex !== null}
                              onClose={handlePendingCloseModal}
                              onSubmit={handlePendingSubmitModal}
                              loading={submitInProgress}
                            />
                            <TableCell sx={{ textAlign: "center" }}>
                              <Radio
                                checked={row.approvedStatus === "approved"}
                                onChange={() => setOpenPendingModalIndex(index)}
                                color="primary"
                                size="small"
                                disabled={
                                  isDisabled ||
                                  hasRowErrors(row) ||
                                  submitInProgress
                                }
                              />
                              {!isDisabled && hasRowErrors(row) && (
                                <span
                                  style={{
                                    fontSize: 18,
                                    color: "gray",
                                    userSelect: "none",
                                  }}
                                >
                                  🚫
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default ManagerExpenseApproval;
